import React, { useEffect, useState, useContext, useMemo } from "react";
import axios from "../utils/axios";
import { AuthContext } from "../context/AuthContext";
import "./styles/ReportViewer.css";
import customerlogo from "./assets/ajay.jpeg";

import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import { Bar } from "react-chartjs-2";
ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend, Title);

export default function TodayReportViewer() {
  const { user, loading, token } = useContext(AuthContext);

  const [masterSites, setMasterSites] = useState([]);
  const [selectedMasterSiteId, setSelectedMasterSiteId] = useState("");
  const [loadingData, setLoadingData] = useState(false);
  const [reports, setReports] = useState([]);
  const [error, setError] = useState("");

  // charts state
  const [top7, setTop7] = useState([]); // [{siteId, site, totalWaterDispensed}]
  const [topMonth, setTopMonth] = useState({ month: "", results: [] });
  const [chartErr7, setChartErr7] = useState("");
  const [chartErrMonth, setChartErrMonth] = useState("");

  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const extractDecimal = (value) => {
    if (value == null) return 0;
    if (typeof value === "object" && value.$numberDecimal) {
      const n = parseFloat(value.$numberDecimal);
      return Number.isFinite(n) ? n : 0;
    }
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : 0;
  };

  const fetchMasterSites = async () => {
    try {
      const res = await axios.get("/mastersites", authHeader);
      const all = Array.isArray(res.data) ? res.data : [];
      const filtered =
        user?.role === "admin"
          ? all
          : all.filter((ms) => (user?.masterSiteAccess || []).includes(ms._id));
      setMasterSites(filtered);
      if (!selectedMasterSiteId && filtered.length > 0) {
        setSelectedMasterSiteId(filtered[0]._id);
      }
    } catch (err) {
      console.error("Error fetching master sites:", err);
      setError("Failed to fetch master sites");
    }
  };

  const fetchTodayReports = async (masterSiteId) => {
    if (!masterSiteId) return;
    setLoadingData(true);
    setError("");
    try {
      const res = await axios.get(`/rep/today/master/${masterSiteId}`, authHeader);
      const arr = Array.isArray(res.data) ? res.data : [];
      setReports(arr);
      if (arr.length === 0) setError("No data available for today.");
    } catch (err) {
      console.error(err);
      setError("Failed to fetch today's report");
    } finally {
      setLoadingData(false);
    }
  };

  // last 7 days
  const fetchTop7 = async (masterSiteId) => {
    if (!masterSiteId) return;
    setChartErr7("");
    try {
      const res = await axios.get(`/last7days/master/${masterSiteId}?days=7`, authHeader);
      setTop7(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Top7 error:", err);
      setChartErr7("Failed to load last 7 days");
      setTop7([]);
    }
  };

  // last month
  const fetchTopMonth = async (masterSiteId) => {
    if (!masterSiteId) return;
    setChartErrMonth("");
    try {
      const res = await axios.get(`/reports/top3/last-month/master/${masterSiteId}`, authHeader);
      const data = res?.data && typeof res.data === "object" ? res.data : { month: "", results: [] };
      setTopMonth(data);
    } catch (err) {
      console.error("TopMonth error:", err);
      setChartErrMonth("Failed to load last month");
      setTopMonth({ month: "", results: [] });
    }
  };

  useEffect(() => {
    if (user) fetchMasterSites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!selectedMasterSiteId) return;
    fetchTodayReports(selectedMasterSiteId);
    fetchTop7(selectedMasterSiteId);
    fetchTopMonth(selectedMasterSiteId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMasterSiteId]);

  const totals = useMemo(() => {
    let totalWater = 0,
      totalAmount = 0,
      cardRechargeAmount = 0,
      cardSwipeAmount = 0;
    for (const r of reports) {
      totalWater += extractDecimal(r.totalWaterDispensed);
      totalAmount += extractDecimal(r.totalAmountCollected);
      cardRechargeAmount += extractDecimal(r.cardRechargeAmount);
      cardSwipeAmount += extractDecimal(r.cardSwipeAmount);
    }
    return {
      totalWater: totalWater.toFixed(1),
      totalAmount: totalAmount.toFixed(2),
      cardRechargeAmount: cardRechargeAmount.toFixed(2),
      cardSwipeAmount: cardSwipeAmount.toFixed(2),
    };
  }, [reports]);

  // ACCORD_THREE_TAP check
  const selectedMasterSite = masterSites.find((ms) => ms._id === selectedMasterSiteId);
  const isThreeTap = selectedMasterSite?.dispenserType === "ACCORD_THREE_TAP";

  // chart helpers
  const toBarData = (items, labelText = "Total Water (L)") => {
    const list = Array.isArray(items) ? items : [];
    const labels = list.map((i) => i.site || "Unknown");
    const values = list.map((i) => parseFloat(i?.totalWaterDispensed) || 0);
    return {
      labels,
      datasets: [
        {
          label: labelText,
          data: values,
        },
      ],
    };
  };

  const commonBarOptions = (title) => ({
    responsive: true,
    maintainAspectRatio: false, // <— important for our .chart-card height
    plugins: {
      title: { display: !!title, text: title, font: { size: 14 } },
      legend: { display: true, position: "top" },
      tooltip: { callbacks: { label: (ctx) => ` ${ctx.dataset.label}: ${ctx.formattedValue} L` } },
    },
    scales: { y: { beginAtZero: true } },
  });

  if (loading) return <p>Loading user...</p>;
  if (!user) return <p>🔒 Not logged in</p>;

  return (
    <div className="report-viewer-container">
      <div className="flex justify-between items-center mb-4">
        {isThreeTap && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "8px 12px",
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              transition: "all 0.3s ease",
              cursor: "pointer",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = "#f0f8ff";
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 4px 10px rgba(0,0,0,0.1)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.transform = "none";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <img
              src={customerlogo}
              alt="Customer Logo"
              style={{ width: 45, height: 45, borderRadius: 8, objectFit: "contain" }}
            />
            <p style={{ margin: 0, fontSize: 15, fontWeight: 500, color: "#222" }}>
              Ajay Infra & Construction
            </p>
          </div>
        )}
        <h2 className="report-heading">Today’s Live Report</h2>
      </div>

      <div className="mb-4">
        <label className="block mb-1 font-medium">Select Master Site:</label>
        <select
          className="border rounded px-2 py-1 w-full"
          value={selectedMasterSiteId}
          onChange={(e) => {
            setSelectedMasterSiteId(e.target.value);
            setReports([]);
            setError("");
          }}
          onBlur={(e) => {
            if (!e.target.value && masterSites[0]?._id) {
              setSelectedMasterSiteId(masterSites[0]._id);
            }
          }}
        >
          {masterSites.map((site) => (
            <option key={site._id} value={site._id}>
              {site.name}
            </option>
          ))}
        </select>
      </div>

      {/* ✅ Responsive charts row */}
      <div className="charts-grid">
        <div className="chart-card">
          <Bar
            data={toBarData(top7, "Total Water (Last 7 Days)")}
            options={commonBarOptions("Top 3 — Last 7 Days")}
          />
        </div>

        <div className="chart-card">
          <Bar
            data={toBarData(topMonth?.results || [], "Total Water (Last Month)")}
            options={commonBarOptions(
              `Top 3 — Last Month${topMonth?.month ? ` (${topMonth.month})` : ""}`
            )}
          />
        </div>
      </div>

      {chartErr7 && <p className="text-red-500 mt-2">{chartErr7}</p>}
      {chartErrMonth && <p className="text-red-500 mt-2">{chartErrMonth}</p>}

      {loadingData && <p>Loading...</p>}
      {error && <p className="text-red-500">{error}</p>}

      {!loadingData && !error && reports.length === 0 && (
        <p className="no-data">No data available for today.</p>
      )}

      {!loadingData && reports.length > 0 && (
        <div className="table-scroll">
          <table className="report-table">
            <thead>
              <tr className="bg-gray-100">
                <th className="border px-2 py-1">Site</th>
                <th className="border px-2 py-1">Water (L)</th>
                <th className="border px-2 py-1">Amount (₹)</th>
                <th className="border px-2 py-1">Card Recharge (₹)</th>
                <th className="border px-2 py-1">Card Swipe (₹)</th>
                <th className="border px-2 py-1">TDS</th>
                <th className="border px-2 py-1">pH</th>
                {isThreeTap && (
                  <>
                    <th className="border px-2 py-1">Tap1 (L)</th>
                    <th className="border px-2 py-1">Tap2 (L)</th>
                    <th className="border px-2 py-1">Tap3 (L)</th>
                    <th className="border px-2 py-1">1000 L Tank % Filled</th>
                    <th className="border px-2 py-1">5000 L Tank % Filled</th>
                    <th className="border px-2 py-1">Cold Temp</th>
                    <th className="border px-2 py-1">Normal Temp</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {reports.map((r, index) => (
                <tr key={index}>
                  <td className="border px-2 py-1">{r.site}</td>
                  <td className="border px-2 py-1">
                    {extractDecimal(r.totalWaterDispensed).toFixed(1)}
                  </td>
                  <td className="border px-2 py-1">
                    {extractDecimal(r.totalAmountCollected).toFixed(2)}
                  </td>
                  <td className="border px-2 py-1">
                    {extractDecimal(r.cardRechargeAmount).toFixed(2)}
                  </td>
                  <td className="border px-2 py-1">
                    {extractDecimal(r.cardSwipeAmount).toFixed(2)}
                  </td>
                  <td className="border px-2 py-1">{extractDecimal(r.tds).toFixed(1)}</td>
                  <td className="border px-2 py-1">{extractDecimal(r.ph).toFixed(1)}</td>
                  {isThreeTap && (
                    <>
                      <td className="border px-2 py-1">
                        {extractDecimal(r.tap1WaterDispensed).toFixed(1)}
                      </td>
                      <td className="border px-2 py-1">
                        {extractDecimal(r.tap2WaterDispensed).toFixed(1)}
                      </td>
                      <td className="border px-2 py-1">
                        {extractDecimal(r.tap3WaterDispensed).toFixed(1)}
                      </td>
                      <td className="border px-2 py-1">{extractDecimal(r.tank1).toFixed(0)}</td>
                      <td className="border px-2 py-1">{extractDecimal(r.tank2).toFixed(0)}</td>
                      <td className="border px-2 py-1">{extractDecimal(r.temp1).toFixed(1)}</td>
                      <td className="border px-2 py-1">{extractDecimal(r.temp2).toFixed(1)}</td>
                    </>
                  )}
                </tr>
              ))}
              <tr className="bg-yellow-100 font-semibold">
                <td className="border px-2 py-1">Total</td>
                <td className="border px-2 py-1">{totals.totalWater}</td>
                <td className="border px-2 py-1">{totals.totalAmount}</td>
                <td className="border px-2 py-1">{totals.cardRechargeAmount}</td>
                <td className="border px-2 py-1">{totals.cardSwipeAmount}</td>
                <td className="border px-2 py-1">-</td>
                <td className="border px-2 py-1">-</td>
                {isThreeTap && (
                  <>
                    <td className="border px-2 py-1">-</td>
                    <td className="border px-2 py-1">-</td>
                    <td className="border px-2 py-1">-</td>
                    <td className="border px-2 py-1">-</td>
                    <td className="border px-2 py-1">-</td>
                    <td className="border px-2 py-1">-</td>
                    <td className="border px-2 py-1">-</td>
                  </>
                )}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
